import { useQuery, UseQueryResult } from "@tanstack/react-query";
import {
  CityEscape,
  DetailedCityEscape,
  DetailedProps,
  Partner,
  Props,
  PropsPartner,
} from "../directus/collections";
import { useDirectus, useIndexedDb } from "../providers";
import {
  getCityEscapeById,
  getCityEscapeDatasource,
} from "../services/cityEscapes";
import { getPartnersByIds } from "../services/partner";
import { getPropsByIds } from "../services/props";

export function useCityEscapesDatasource() {
  const { client } = useDirectus();
  const { getDb } = useIndexedDb();
  return useQuery({
    queryKey: ["cityEscapesDatasource"],
    queryFn: async () => {
      const db = await getDb();
      try {
        const datasource = await getCityEscapeDatasource(client, db);
        return datasource.sort((a, b) => a.label.localeCompare(b.label));
      } finally {
        db.close();
      }
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useFullCityEscapeData(
  cityEscapeId?: string | null
): UseQueryResult<DetailedCityEscape> {
  const { client } = useDirectus();
  return useQuery({
    queryKey: ["fullCityEscapeData", cityEscapeId],
    queryFn: async () => {
      if (!cityEscapeId) {
        throw new Error("cityEscapeId is required");
      }
      const cityEscape: CityEscape = await getCityEscapeById(
        client,
        cityEscapeId
      );
      const props = await getPropsByIds(client, {
        propsIds: cityEscape.props.map((p) => p.props_id),
      });
      const detailedProps: DetailedProps[] = [];

      const partners = await getPartnersByIds(client, {
        partnerIds: props
          .flatMap((p: Props) => p.partners)
          .flatMap((partner: PropsPartner) => partner.partners_id),
      });

      for (const prop of props) {
        detailedProps.push({
          ...prop,
          partners: prop.partners.map((partner: PropsPartner) => {
            return partners.find((p) => p.id === partner.partners_id)!;
          }) as Partner[],
        });
      }

      return { ...cityEscape, props: detailedProps };
    },
    enabled: !!cityEscapeId,
  });
}
